import { reloadApp } from '../../hooks/useVersionCheck'

/**
 * Initializes a global error listener to catch ChunkLoadError or SyntaxError.
 *
 * WHY THIS IS NEEDED:
 * Modern SPAs (like ours) use Code Splitting. When we roll out a new deployment,
 * old JavaScript files (chunks) are removed from the server. If a user has an old tab open
 * and tries to navigate to a new page, the browser requests the old chunk file.
 *
 * The server, not finding the file, serves index.html (per SPA routing rules).
 * The browser tries to parse this HTML as JS and fails with: "SyntaxError: Unexpected token '<'".
 *
 * THIS LISTENER:
 * 1. Catches such errors in real-time.
 * 2. Verifies that the error occurred in a JS file.
 * 3. Triggers a soft application reload via reloadApp() so the browser downloads the new code version.
 * 4. Considers safety: if an audio recording is in progress, the reload will be postponed.
 */
// Messages browsers use when a dynamic import() (React.lazy) fails to load its chunk.
// Chrome: "Failed to fetch dynamically imported module: <url>"
// Firefox: "error loading dynamically imported module: <url>"
// Safari: "Importing a module script failed."
const DYNAMIC_IMPORT_ERROR_MESSAGES = [
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'Importing a module script failed',
]

const isDynamicImportError = (message: string | undefined): boolean =>
    !!message && DYNAMIC_IMPORT_ERROR_MESSAGES.some((m) => message.includes(m))

const isChunkError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false
    const e = error as { name?: string; message?: string }
    return (
        e.name === 'ChunkLoadError' ||
        (e instanceof SyntaxError && !!e.message?.includes("Unexpected token '<'")) ||
        isDynamicImportError(e.message)
    )
}

export const initChunkErrorListener = () => {
    // Synchronous errors (e.g. script tag parse failures)
    window.addEventListener('error', (event) => {
        const messageMatch =
            event.message?.includes("Unexpected token '<'") ||
            event.message?.includes('Load chunk') ||
            event.error?.name === 'ChunkLoadError' ||
            isDynamicImportError(event.message)

        const isJsChunk =
            event.filename?.includes('/assets/') || event.filename?.includes('/static/js/') || !event.filename

        if (messageMatch && isJsChunk) {
            console.warn('[ChunkErrorListener] Chunk loading error detected, triggering app reload...', {
                message: event.message,
                filename: event.filename,
            })
            reloadApp()
        }
    })

    // Dynamic import() failures arrive as unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        if (isChunkError(event.reason)) {
            console.warn('[ChunkErrorListener] Chunk promise rejection detected, triggering app reload...', {
                reason: event.reason,
            })
            reloadApp()
        }
    })

    // Vite-specific preload error event
    window.addEventListener('vite:preloadError', () => {
        console.warn('[ChunkErrorListener] Vite preload error detected, triggering app reload...')
        reloadApp()
    })
}
